import React, { useState } from "react";
import Select from "react-select";
import { useDropzone } from "react-dropzone";

// Define types for the form configuration
type FieldType = 'text' | 'number' | 'select' | 'file';

interface FieldConfig {
    type: FieldType;
    label: string;
    name: string;
    description?: string;
    options?: string[]; // Only for select fields
}

interface StepConfig {
    step: number;
    fields: FieldConfig[];
}

// Define the shape of the form data
interface FormData {
    [key: string]: string | number | File[] | null | { label: string, value: string } | undefined;
}

const NewApplicationPage: React.FC = () => {
    // Example configuration array for dynamic form generation
    const formConfig: StepConfig[] = [
        {
            step: 1,
            fields: [
                { type: 'text', label: 'First name', name: 'firstName' },
                { type: 'text', label: 'Last name', name: 'lastName' },
                { type: 'text', label: 'Middle name', name: 'middleName' },
            ],
        },
        {
            step: 2,
            fields: [
                { type: 'text', label: 'Email', name: 'email' },
                { type: 'number', label: 'Phone number', name: 'phoneNumber' },
                { type: 'text', label: 'Home address', name: 'address' },
                { type: 'text', label: 'Date of birth', name: 'dateOfBirth' },
            ],
        },
        {
            step: 3,
            fields: [
                { type: 'select', label: 'Current education level', name: 'educationLevel', options: ['High School', 'Undergraduate', 'Graduate', 'Other'] },
                { type: 'text', label: 'Intended course of study', name: 'course' },
                { type: 'text', label: 'Intended institution of study', name: 'institution' },
            ],
        },
        {
            step: 4,
            fields: [
                { type: 'select', label: 'Current employment status', name: 'employmentStatus', options: ['Employed', 'Unemployed', 'Student'] },
                { type: 'select', label: 'How much do you currently earn', name: 'salary', options: ['< $500', '$500 - $1000', '$1000 - $2000', '> $2000'] },
                { type: 'text', label: 'Reason for financial support', name: 'reason' },
            ],
        },
        {
            step: 5,
            fields: [
                { type: 'text', label: 'Full name of first guardian', name: 'firstGuardianName' },
                { type: 'number', label: 'Phone number of first guardian', name: 'firstGuardianPhone' },
            ],
        },
        {
            step: 6,
            fields: [
                { type: 'text', label: 'Full name of second guardian', name: 'secondGuardianName' },
                { type: 'number', label: 'Phone number of second guardian', name: 'secondGuardianPhone' },
            ],
        },
        {
            step: 7,
            fields: [
                { type: 'file', label: 'Upload academic documents', name: 'academicDocs', description: 'Please upload your academic certificates and transcripts.' },
            ],
        },
        {
            step: 8,
            fields: [
                { type: 'file', label: 'Upload employment documents', name: 'employmentDocs', description: 'Please upload your employment documentation (if applicable).' },
            ],
        },
        {
            step: 9,
            fields: [
                { type: 'file', label: 'Upload Ghana card', name: 'ghanaCard' },
            ],
        },
        {
            step: 10,
            fields: [
                { type: 'file', label: 'Upload recent passport size photograph', name: 'passportPhoto' },
            ],
        },
        {
            step: 11,
            fields: [
                { type: 'file', label: 'Upload proof of residence', name: 'residenceProof', description: 'Please upload proof of residence, such as a utility bill or address verification.' },
            ],
        },
    ];

    const [currentStep, setCurrentStep] = useState<number>(1);
    const [formData, setFormData] = useState<FormData>({});

    // Handle input change for text/number fields
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, field: FieldConfig) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Handle react-select changes
    const handleSelectChange = (selectedOption: { label: string, value: string } | null, field: FieldConfig) => {
        setFormData({ ...formData, [field.name]: selectedOption });
    };

    // Handle file upload using react-dropzone
    const handleDrop = (acceptedFiles: File[], field: FieldConfig) => {
        setFormData({ ...formData, [field.name]: acceptedFiles });
    };

    // Dropzone Component for File Upload
    const FileUpload = ({ field }: { field: FieldConfig }) => {
        const { getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop: (acceptedFiles) => handleDrop(acceptedFiles, field),
            multiple: true,
        });

        return (
            <div {...getRootProps()} className="border-dashed border-2 border-gray-300 p-4 rounded-md text-center cursor-pointer">
                <input {...getInputProps()} />
                {isDragActive ? (
                    <p>Drop the files here...</p>
                ) : (
                    <p>Drag & drop files here, or click to select files</p>
                )}
            </div>
        );
    };

    // Move to the next step
    const nextStep = () => {
        if (currentStep < formConfig.length) {
            setCurrentStep(currentStep + 1);
        }
    };

    // Move to the previous step
    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    // Render form fields based on the current step
    const renderFields = () => {
        const stepConfig = formConfig.find(config => config.step === currentStep);
        if (!stepConfig) return null;

        return stepConfig.fields.map((field, index) => (
            <div key={index} className="mb-4">
                <label className="block text-gray-700 mb-2">{field.label}</label>
                {field.description && <p className="text-sm text-gray-500 mb-2">{field.description}</p>}
                {(() => {
                    switch (field.type) {
                        case 'text':
                        case 'number':
                            return (
                                <input
                                    type={field.type}
                                    name={field.name}
                                    value={(formData[field.name] as string) || ''}
                                    onChange={(e) => handleChange(e, field)}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-300"
                                />
                            );
                        case 'select':
                            return (
                                <Select
                                    name={field.name}
                                    options={field.options?.map(option => ({ label: option, value: option })) || []}
                                    value={formData[field.name] as { label: string; value: string } | null}
                                    onChange={(selectedOption) => handleSelectChange(selectedOption, field)}
                                    className="w-full"
                                />
                            );
                        case 'file':
                            return <FileUpload field={field} />;
                        default:
                            return null;
                    }
                })()}
            </div>
        ));
    };
    return (
        <div className="w-full h-full flex items-center justify-center">
            <div className="w-full bg-white h-[95%] shadow rounded-md py-10 px-4 md:px-20 md:w-[70%]">
                <div className="w-full">
                    <h2 className="text-[2rem] font-bold mb-6">New Application</h2>

                    {/* Step Progress Indicator */}
                    <div className="mb-6 text-center">
                        <p className="text-gray-600">Step {currentStep} of {formConfig.length}</p>
                        <div className="w-full bg-gray-200 h-2 rounded-full mt-2">
                            <div
                                className="bg-primary h-2 rounded-full"
                                style={{ width: `${(currentStep / formConfig.length) * 100}%` }}
                            />
                        </div>
                    </div>

                    {/* Render Form Fields */}
                    {renderFields()}

                    {/* Navigation Buttons */}
                    <div className="flex justify-between mt-6">
                        {currentStep > 1 && (
                            <button
                                onClick={prevStep}
                                className="border border-primary text-black px-4 py-2 rounded-md hover:border-yellow-600 w-[30%] md:w-[20%]"
                            >
                                Previous
                            </button>
                        )}
                        {currentStep < formConfig.length ? (
                            <button
                                onClick={nextStep}
                                className="bg-primary text-black px-4 py-2 rounded-md hover:bg-yellow-600 w-[30%] md:w-[20%]"
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                onClick={() => alert(JSON.stringify(formData))}
                                className="bg-primary text-black px-4 py-2 rounded-md hover:bg-yellow-600 w-[30%] md:w-[20%]"
                            >
                                Submit
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>

    );
};

export default NewApplicationPage;
